/**
 * State machine for the practice-session lifecycle.
 *
 * Sits one level above review-machine.ts: a session is a sequence of
 * exercises, each of which goes through the practice machine's
 * ready→playing→awaiting→graded loop. This reducer tracks the queue, the
 * running stats, and the three session phases:
 *
 *   idle    → no active session yet (first visit or after a summary is dismissed)
 *   running → session is active; items are being drilled
 *   summary → queue drained; SessionSummary is showing
 *
 * Pure: no React, no audio, no I/O. The page calls
 * persistence.startSession / recordReview / endSession; this reducer is
 * just the in-memory mirror.
 */

import type { Item, Rating } from "@music-app/learning-system";

export type SessionPhase = "idle" | "running" | "summary";

export interface SessionState {
  phase: SessionPhase;
  /** DB session record ID. Null in `idle`. */
  sessionId: string | null;
  /** performance.now() at session start — used for duration. */
  startedAtPerf: number;
  /** Wall-clock ISO at session start — used for display / persistence. */
  startedAtIso: string | null;
  /** Items still to drill. Head is consumed when the next exercise starts. */
  queue: Item[];
  /** Original session size, captured at start. Updated by REBALANCE to reflect
   *  the new total so the "X of Y" counter stays accurate. */
  totalItems: number;
  itemsReviewed: number;
  correctCount: number;
  /** Sum of response times in ms; divide by itemsReviewed for the average. */
  responseMsTotal: number;
  /** FSRS rating counts accumulated over the session. */
  ratingCounts: { again: number; hard: number; good: number; easy: number };
  /** Total items touched by any REBALANCE action (removed + added), for diagnostics. */
  itemsRebalanced: number;
}

export type SessionAction =
  | {
      type: "SESSION_STARTED";
      id: string;
      queue: Item[];
      atPerf: number;
      atIso: string;
    }
  | {
      /**
       * Rehydrate from a persisted SessionRecord. Differs from
       * SESSION_STARTED in that prior stats (itemsReviewed, correctCount,
       * responseMsTotal) are restored from disk.
       */
      type: "SESSION_RESUMED";
      id: string;
      queue: Item[];
      atPerf: number;
      atIso: string;
      itemsReviewed: number;
      correctCount: number;
      responseMsTotal: number;
    }
  | { type: "ITEM_CONSUMED" }
  | {
      type: "REVIEW_RECORDED";
      correct: boolean;
      responseMs: number;
      rating: Rating;
    }
  | {
      type: "REBALANCE";
      /** Items to append to the end of the queue. */
      additionalItems: Item[];
      /** IDs of items to remove from the queue (new-state items being swapped out). */
      removeItemIds: string[];
    }
  | { type: "SESSION_ENDED" }
  | { type: "SESSION_DISMISSED" };

const initialRatingCounts = { again: 0, hard: 0, good: 0, easy: 0 };

export const initialSessionState: SessionState = {
  phase: "idle",
  sessionId: null,
  startedAtPerf: 0,
  startedAtIso: null,
  queue: [],
  totalItems: 0,
  itemsReviewed: 0,
  correctCount: 0,
  responseMsTotal: 0,
  ratingCounts: initialRatingCounts,
  itemsRebalanced: 0,
};

export function sessionReducer(state: SessionState, action: SessionAction): SessionState {
  switch (action.type) {
    case "SESSION_STARTED":
      return {
        phase: "running",
        sessionId: action.id,
        startedAtPerf: action.atPerf,
        startedAtIso: action.atIso,
        queue: action.queue,
        totalItems: action.queue.length,
        itemsReviewed: 0,
        correctCount: 0,
        responseMsTotal: 0,
        ratingCounts: initialRatingCounts,
        itemsRebalanced: 0,
      };

    case "SESSION_RESUMED":
      return {
        phase: "running",
        sessionId: action.id,
        startedAtPerf: action.atPerf,
        startedAtIso: action.atIso,
        queue: action.queue,
        // Best-effort reconstruction: a session abandoned mid-exercise loses
        // the in-flight item, so this can be off by one for those resumes.
        totalItems: action.itemsReviewed + action.queue.length,
        itemsReviewed: action.itemsReviewed,
        correctCount: action.correctCount,
        responseMsTotal: action.responseMsTotal,
        ratingCounts: initialRatingCounts,
        itemsRebalanced: 0,
      };

    case "ITEM_CONSUMED":
      if (state.phase !== "running" || state.queue.length === 0) return state;
      return { ...state, queue: state.queue.slice(1) };

    case "REVIEW_RECORDED":
      if (state.phase !== "running") return state;
      return {
        ...state,
        itemsReviewed: state.itemsReviewed + 1,
        correctCount: state.correctCount + (action.correct ? 1 : 0),
        responseMsTotal: state.responseMsTotal + action.responseMs,
        ratingCounts: {
          ...state.ratingCounts,
          [action.rating]: state.ratingCounts[action.rating] + 1,
        },
      };

    case "REBALANCE": {
      if (state.phase !== "running") return state;
      const { additionalItems, removeItemIds } = action;
      const removeSet = new Set(removeItemIds);
      const filteredQueue = state.queue.filter((item) => !removeSet.has(item.id));
      const newQueue = [...filteredQueue, ...additionalItems];
      return {
        ...state,
        queue: newQueue,
        totalItems: state.totalItems - removeItemIds.length + additionalItems.length,
        itemsRebalanced: state.itemsRebalanced + removeItemIds.length + additionalItems.length,
      };
    }

    case "SESSION_ENDED":
      if (state.phase !== "running") return state;
      return { ...state, phase: "summary" };

    case "SESSION_DISMISSED":
      return initialSessionState;
  }
}

/* ─── Selectors ───────────────────────────────────────────────────────── */

/** Peek the next item to drill without removing it. */
export function nextItem(state: SessionState): Item | null {
  return state.queue[0] ?? null;
}

export function averageResponseMs(state: SessionState): number {
  return state.itemsReviewed > 0 ? state.responseMsTotal / state.itemsReviewed : 0;
}

export function isSessionActive(state: SessionState): boolean {
  return state.phase === "running";
}

/** Final stats payload to persist on SESSION_ENDED. */
export function sessionStats(state: SessionState): {
  itemsReviewed: number;
  correctCount: number;
  averageResponseMs: number;
  ratingCounts: { again: number; hard: number; good: number; easy: number };
} {
  return {
    itemsReviewed: state.itemsReviewed,
    correctCount: state.correctCount,
    averageResponseMs: averageResponseMs(state),
    ratingCounts: state.ratingCounts,
  };
}
